// Глобальный компонент режима изучения (Этап 46.13).
// Монтируется один раз в App.tsx. Слушает клики на document с capture,
// перехватывает их если active=true и клик попал на [data-tt].

import { useEffect, useMemo } from 'react';
import { useStudyMode } from './useStudyMode';
import { buildTooltips } from './tooltips';
import { useTr } from '../i18n/useTr';
import styles from './StudyOverlay.module.css';

export function StudyOverlay() {
  const active = useStudyMode(s => s.active);
  const openTtKey = useStudyMode(s => s.openTtKey);
  const openTooltip = useStudyMode(s => s.openTooltip);
  const closeTooltip = useStudyMode(s => s.closeTooltip);
  const disable = useStudyMode(s => s.disable);
  const tr = useTr();

  // Глобальный capture-listener: перехватывает клики на [data-tt]
  // и блокирует их обычное действие, показывая tooltip.
  // [data-tt-exit] = кнопка выхода из режима — её клики НЕ перехватываем,
  // она сама дёрнет toggleStudyMode() через свой onClick.
  useEffect(() => {
    if (!active) return;
    const onClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      // Если клик на (или внутри) элемента выхода — пропускаем, пусть
      // React-обработчик сам отработает toggleStudy().
      if (el?.closest?.('[data-tt-exit]')) return;
      const target = el?.closest?.('[data-tt]') as HTMLElement | null;
      if (!target) return;
      const key = target.dataset.tt;
      if (!key) return;
      e.preventDefault();
      e.stopPropagation();
      openTooltip(key);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [active, openTooltip]);

  // Escape выходит из режима.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (openTtKey) closeTooltip();
        else disable();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, openTtKey, closeTooltip, disable]);

  // useMemo по tr — словарь пересобирается только при смене языка.
  const tooltips = useMemo(() => buildTooltips(tr), [tr]);

  if (!active) return null;

  const tt = openTtKey ? tooltips[openTtKey] : null;

  return (
    <>
      {/* Затемнение фона. pointer-events: none чтобы клики проходили
          сквозь до подсвеченных [data-tt] элементов. */}
      <div className={styles.backdrop} />

      {/* Подсказка-баннер сверху */}
      <div className={styles.banner}>
        <span className={styles.dot} />
        {tr('РЕЖИМ ИЗУЧЕНИЯ — ТАПНИ ПОДСВЕЧЕННЫЙ ЭЛЕМЕНТ', 'STUDY MODE — TAP A HIGHLIGHTED ELEMENT')}
      </div>

      {/* Tooltip popup при тапе на элемент */}
      {tt && (
        <div className={styles.ttBackdrop} onClick={closeTooltip}>
          <div className={styles.ttCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.ttTag}>{tr('ОПИСАНИЕ ЭЛЕМЕНТА', 'ELEMENT DESCRIPTION')}</div>
            <h3 className={styles.ttTitle}>{tt.title}</h3>
            <div className={styles.ttText} dangerouslySetInnerHTML={{ __html: tt.text }} />
            <button className={styles.ttClose} onClick={closeTooltip}>{tr('ПОНЯТНО', 'GOT IT')}</button>
          </div>
        </div>
      )}
    </>
  );
}
